<template>
  <div class="modal fade" id="reportDetailsModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header bg-info text-white">
          <h5 class="modal-title">
            <i class="bx bx-file-blank me-2"></i>Cash Collection Report Details
          </h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3">Loading report details...</p>
          </div>

          <div v-else-if="reportData">
            <!-- Report Header -->
            <div class="row mb-4">
              <div class="col-md-8">
                <div class="card border-primary">
                  <div class="card-header bg-primary text-white">
                    <h6 class="card-title mb-0">
                      <i class="bx bx-info-circle me-2"></i>Report Information
                    </h6>
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <div class="col-md-6">
                        <p><strong>Report Reference:</strong> {{ reportData.report_reference }}</p>
                        <p><strong>Collector:</strong> {{ reportData.collector_name }}</p>
                        <p><strong>Collection Date:</strong> {{ formatDate(reportData.collection_date) }}</p>
                        <p><strong>Collection Point:</strong> {{ reportData.collection_point }}</p>
                        <p><strong>Status:</strong>
                          <span class="badge ms-2" :class="getStatusClass(reportData.status)">
                            {{ reportData.status.toUpperCase() }}
                          </span>
                        </p>
                      </div>
                      <div class="col-md-6">
                        <p><strong>Created:</strong> {{ formatDateTime(reportData.created_at) }}</p>
                        <p v-if="reportData.submission_date"><strong>Submitted:</strong> {{ formatDateTime(reportData.submission_date) }}</p>
                        <p v-if="reportData.reconciliation_date"><strong>Reconciled:</strong> {{ formatDateTime(reportData.reconciliation_date) }}</p>
                        <p v-if="reportData.reconciled_by"><strong>Reconciled By:</strong> {{ reportData.reconciled_by_name || 'Unknown' }}</p>
                        <p v-if="reportData.bank_deposit_reference"><strong>Bank Reference:</strong> {{ reportData.bank_deposit_reference }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-4">
                <div class="card border-success">
                  <div class="card-header bg-success text-white">
                    <h6 class="card-title mb-0">
                      <i class="bx bx-calculator me-2"></i>Financial Summary
                    </h6>
                  </div>
                  <div class="card-body text-center">
                    <div class="mb-3">
                      <h4 class="text-primary">ETB {{ formatCurrency(reportData.total_amount_collected) }}</h4>
                      <small class="text-muted">Expected Amount</small>
                    </div>
                    <div class="mb-3" v-if="reportData.amount_submitted > 0">
                      <h4 class="text-info">ETB {{ formatCurrency(reportData.amount_submitted) }}</h4>
                      <small class="text-muted">Submitted Amount</small>
                    </div>
                    <div class="border-top pt-3" v-if="reportData.amount_submitted > 0">
                      <h5 :class="varianceClass">
                        {{ reportData.variance_amount >= 0 ? '+' : '' }}ETB {{ formatCurrency(Math.abs(reportData.variance_amount)) }}
                      </h5>
                      <small class="text-muted">{{ varianceText }}</small>
                    </div>
                    <div class="mt-3">
                      <div class="d-flex justify-content-between">
                        <span>Transactions:</span>
                        <strong>{{ reportData.total_transactions }}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Payment Method Breakdown -->
            <div class="card mb-4" v-if="paymentBreakdown.length > 0">
              <div class="card-header">
                <h6 class="card-title mb-0">
                  <i class="bx bx-pie-chart-alt me-2"></i>Payment Method Breakdown
                </h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-8">
                    <div class="table-responsive">
                      <table class="table table-sm">
                        <thead class="table-light">
                        <tr>
                          <th>Payment Method</th>
                          <th class="text-center">Count</th>
                          <th class="text-end">Amount</th>
                          <th class="text-end">Percentage</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="method in paymentBreakdown" :key="method.method">
                          <td>
                            <i :class="getPaymentMethodIcon(method.method)" class="me-2"></i>
                            {{ method.method }}
                          </td>
                          <td class="text-center">{{ method.count }}</td>
                          <td class="text-end">
                            <strong>ETB {{ formatCurrency(method.amount) }}</strong>
                          </td>
                          <td class="text-end">{{ method.percentage.toFixed(1) }}%</td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <!-- Simple visual representation -->
                    <div class="payment-method-chart">
                      <div v-for="method in paymentBreakdown" :key="method.method" class="chart-item mb-2">
                        <div class="d-flex justify-content-between align-items-center mb-1">
                          <small>{{ method.method }}</small>
                          <small>{{ method.percentage.toFixed(1) }}%</small>
                        </div>
                        <div class="progress" style="height: 8px;">
                          <div class="progress-bar"
                               :class="getProgressBarClass(method.method)"
                               :style="{ width: method.percentage + '%' }">
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Transaction Details -->
            <div class="card mb-4">
              <div class="card-header">
                <div class="d-flex justify-content-between align-items-center">
                  <h6 class="card-title mb-0">
                    <i class="bx bx-list-ul me-2"></i>Transaction Details
                  </h6>
                  <div class="d-flex gap-2">
                    <button class="btn btn-outline-primary btn-sm" @click="exportTransactions">
                      <i class="bx bx-export me-1"></i>Export
                    </button>
                    <span class="badge bg-primary">{{ transactions.length }} Transactions</span>
                  </div>
                </div>
              </div>
              <div class="card-body">
                <!-- Search and Filter -->
                <div class="row mb-3">
                  <div class="col-md-4">
                    <input
                        type="text"
                        v-model="searchQuery"
                        class="form-control form-control-sm"
                        placeholder="Search by member name, CBHI number, or receipt..."
                    >
                  </div>
                  <div class="col-md-3">
                    <select v-model="filterMethod" class="form-select form-select-sm">
                      <option value="">All Payment Methods</option>
                      <option v-for="method in uniquePaymentMethods" :key="method" :value="method">
                        {{ method }}
                      </option>
                    </select>
                  </div>
                  <div class="col-md-3">
                    <select v-model="filterStatus" class="form-select form-select-sm">
                      <option value="">All Status</option>
                      <option value="included">Included in Submission</option>
                      <option value="excluded">Excluded from Submission</option>
                    </select>
                  </div>
                  <div class="col-md-2">
                    <button class="btn btn-outline-secondary btn-sm w-100" @click="clearFilters">
                      <i class="bx bx-x me-1"></i>Clear
                    </button>
                  </div>
                </div>

                <div class="table-responsive">
                  <table class="table table-hover table-sm">
                    <thead class="table-light">
                    <tr>
                      <th width="5%">#</th>
                      <th>Member Details</th>
                      <th>Receipt</th>
                      <th>Payment Method</th>
                      <th>Collection Time</th>
                      <th class="text-end">Amount</th>
                      <th class="text-center">Status</th>
                      <th class="text-center">Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(transaction, index) in filteredTransactions" :key="transaction.payment_id">
                      <td>{{ index + 1 }}</td>
                      <td>
                        <div>
                          <strong>{{ transaction.member_name }}</strong>
                          <br>
                          <small class="text-muted">{{ transaction.cbhi_number }}</small>
                        </div>
                      </td>
                      <td>
                        <strong>{{ transaction.receipt_number }}</strong>
                      </td>
                      <td>
                        <i :class="getPaymentMethodIcon(transaction.payment_method)" class="me-1"></i>
                        {{ transaction.payment_method }}
                      </td>
                      <td>{{ formatDateTime(transaction.collection_time) }}</td>
                      <td class="text-end">
                        <strong>ETB {{ formatCurrency(transaction.payment_amount) }}</strong>
                      </td>
                      <td class="text-center">
                          <span class="badge" :class="transaction.included_in_submission ? 'bg-success' : 'bg-warning'">
                            <i :class="transaction.included_in_submission ? 'bx bx-check' : 'bx bx-x'" class="me-1"></i>
                            {{ transaction.included_in_submission ? 'Included' : 'Excluded' }}
                          </span>
                      </td>
                      <td class="text-center">
                        <button class="btn btn-outline-primary btn-sm"
                                title="View Payment Details"
                                @click="viewPaymentDetails(transaction)">
                          <i class="bx bx-show"></i>
                        </button>
                      </td>
                    </tr>
                    </tbody>
                    <tfoot class="table-light" v-if="filteredTransactions.length > 0">
                    <tr>
                      <td colspan="5"><strong>Filtered Total</strong></td>
                      <td class="text-end">
                        <strong class="text-primary">ETB {{ formatCurrency(filteredTotal) }}</strong>
                      </td>
                      <td colspan="2"></td>
                    </tr>
                    </tfoot>
                  </table>

                  <div v-if="filteredTransactions.length === 0" class="text-center py-5 text-muted">
                    <i class="bx bx-search display-4 mb-3"></i>
                    <p>No transactions found matching your criteria</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Notes Section -->
            <div class="card" v-if="reportData.notes || auditLog.length > 0">
              <div class="card-header">
                <h6 class="card-title mb-0">
                  <i class="bx bx-note me-2"></i>Notes & Audit Log
                </h6>
              </div>
              <div class="card-body">
                <div v-if="reportData.notes" class="mb-4">
                  <h6>Report Notes:</h6>
                  <div class="bg-light p-3 rounded">
                    {{ reportData.notes }}
                  </div>
                </div>

                <div v-if="auditLog.length > 0">
                  <h6>Audit Trail:</h6>
                  <div class="timeline">
                    <div v-for="log in auditLog" :key="log.log_id" class="timeline-item">
                      <div class="timeline-marker"></div>
                      <div class="timeline-content">
                        <div class="d-flex justify-content-between">
                          <strong>{{ log.action }}</strong>
                          <small class="text-muted">{{ formatDateTime(log.created_at) }}</small>
                        </div>
                        <p class="mb-1">
                          Status changed from <span class="badge bg-secondary">{{ log.old_status || 'N/A' }}</span>
                          to <span class="badge bg-primary">{{ log.new_status }}</span>
                        </p>
                        <p class="mb-1">By: {{ log.performed_by_name }}</p>
                        <p v-if="log.notes" class="text-muted small">{{ log.notes }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
            <i class="bx bx-x me-1"></i>Close
          </button>
          <button type="button" class="btn btn-outline-primary" @click="downloadReport">
            <i class="bx bx-download me-1"></i>Download Report
          </button>
          <button type="button" class="btn btn-primary" @click="printReport">
            <i class="bx bx-printer me-1"></i>Print Report
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { authUrl, executeJson } from "@/api";

export default {
  name: "ReportDetailsModal",
  data() {
    return {
      modalInstance: null,
      loading: false,
      reportData: null,
      transactions: [],
      paymentBreakdown: [],
      auditLog: [],
      searchQuery: '',
      filterMethod: '',
      filterStatus: ''
    };
  },

  computed: {
    varianceClass() {
      if (!this.reportData) return 'text-muted';
      if (Math.abs(this.reportData.variance_amount) < 0.01) return 'text-success';
      return this.reportData.variance_amount > 0 ? 'text-warning' : 'text-danger';
    },

    varianceText() {
      if (!this.reportData) return '';
      if (Math.abs(this.reportData.variance_amount) < 0.01) return 'Exact Match';
      return this.reportData.variance_amount > 0 ? 'Overage' : 'Shortage';
    },

    filteredTransactions() {
      let filtered = this.transactions;

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(t =>
            t.member_name.toLowerCase().includes(query) ||
            t.cbhi_number.toLowerCase().includes(query) ||
            t.receipt_number.toLowerCase().includes(query)
        );
      }

      if (this.filterMethod) {
        filtered = filtered.filter(t => t.payment_method === this.filterMethod);
      }

      if (this.filterStatus) {
        const included = this.filterStatus === 'included';
        filtered = filtered.filter(t => t.included_in_submission === included);
      }

      return filtered;
    },

    filteredTotal() {
      return this.filteredTransactions
          .filter(t => t.included_in_submission)
          .reduce((sum, t) => sum + parseFloat(t.payment_amount || 0), 0);
    },

    uniquePaymentMethods() {
      return [...new Set(this.transactions.map(t => t.payment_method))];
    }
  },

  mounted() {
    if (typeof window !== 'undefined' && window.bootstrap) {
      this.modalInstance = new window.bootstrap.Modal(document.getElementById('reportDetailsModal'));
    }
  },

  methods: {
    async openModal(report) {
      this.loading = true;
      this.reportData = report;
      this.clearFilters();

      if (this.modalInstance) {
        this.modalInstance.show();
      }

      try {
        await this.loadReportDetails();
        await this.loadAuditLog();
      } catch (error) {
        console.error('Error loading report details:', error);
      } finally {
        this.loading = false;
      }
    },

    async loadReportDetails() {
      const data = {
        "function": "getCashCollectionReportDetails",
        "reportId": this.reportData.report_id
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.transactions = res.data.data.transactions || [];
          this.paymentBreakdown = res.data.data.paymentBreakdown || [];
        }
      } catch (e) {
        console.error("Failed to load report details:", e);
      }
    },

    async loadAuditLog() {
      const data = {
        "function": "getCashCollectionAuditLog",
        "reportId": this.reportData.report_id
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.auditLog = res.data.data.auditLog || [];
        }
      } catch (e) {
        console.error("Failed to load audit log:", e);
      }
    },

    getStatusClass(status) {
      const classes = {
        'pending': 'bg-warning',
        'submitted': 'bg-info',
        'reconciled': 'bg-success',
        'disputed': 'bg-danger'
      };
      return classes[status] || 'bg-secondary';
    },

    getPaymentMethodIcon(method) {
      const methodLower = method.toLowerCase();
      if (methodLower.includes('cash')) return 'bx bx-money text-warning';
      if (methodLower.includes('telebirr') || methodLower.includes('mpesa') || methodLower.includes('cbe')) {
        return 'bx bx-mobile-alt text-success';
      }
      if (methodLower.includes('bank')) return 'bx bx-building-house text-primary';
      return 'bx bx-credit-card text-info';
    },

    getProgressBarClass(method) {
      const methodLower = method.toLowerCase();
      if (methodLower.includes('cash')) return 'bg-warning';
      if (methodLower.includes('telebirr') || methodLower.includes('mpesa') || methodLower.includes('cbe')) {
        return 'bg-success';
      }
      if (methodLower.includes('bank')) return 'bg-primary';
      return 'bg-info';
    },

    clearFilters() {
      this.searchQuery = '';
      this.filterMethod = '';
      this.filterStatus = '';
    },

    viewPaymentDetails(transaction) {
      // Emit event to parent or open another modal for payment details
      this.$emit('view-payment', transaction);
    },

    exportTransactions() {
      // Simple CSV export
      const headers = ['#', 'Member Name', 'CBHI Number', 'Receipt', 'Payment Method', 'Amount', 'Time', 'Status'];
      const rows = this.filteredTransactions.map((t, index) => [
        index + 1,
        t.member_name,
        t.cbhi_number,
        t.receipt_number,
        t.payment_method,
        t.payment_amount,
        this.formatDateTime(t.collection_time),
        t.included_in_submission ? 'Included' : 'Excluded'
      ]);

      const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.reportData.report_reference}_transactions.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    },

    downloadReport() {
      // Implement download functionality
      console.log('Downloading report:', this.reportData.report_reference);
    },

    printReport() {
      // Implement print functionality
      window.print();
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat().format(amount || 0);
    }
  }
};
</script>

<style scoped>
.btn-close-white {
  filter: brightness(0) invert(1);
}

.modal-body {
  max-height: 80vh;
  overflow-y: auto;
}

.progress {
  border-radius: 4px;
}

.timeline {
  position: relative;
  padding-left: 2rem;
}

.timeline-item {
  position: relative;
  margin-bottom: 1.5rem;
}

.timeline-marker {
  position: absolute;
  left: -2rem;
  top: 0.25rem;
  width: 12px;
  height: 12px;
  background: #007bff;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 0 0 2px #007bff;
}

.timeline-content {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 0.375rem;
  border-left: 3px solid #007bff;
}

.timeline::before {
  content: '';
  position: absolute;
  left: -1.5rem;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e9ecef;
}

.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}

.badge {
  font-size: 0.75rem;
  padding: 0.35rem 0.65rem;
}

.payment-method-chart .chart-item {
  margin-bottom: 1rem;
}
</style>